import {
  AssemblyJourneys,
  SectionJourney,
} from 'src/constants/assemblyJourneys';
import {
  ComponentTypes,
  DyProductSource,
  GlobalComponentsScreenInterface,
  GlobalComponentsScreensInterface,
  IDisclaimerPosition,
  IDisclaimerWidth,
  IGridDimensionsParams,
  ILayout,
  SectionTypes,
} from 'src/interfaces';

import { lang } from './language';
import { checkSectionTypes } from './section-types-helper';

export interface IParseLayoutConfigReturn {
  orientation: string;
  rows: number;
  cols: number;
  disclaimerPosition: string | null;
  disclaimerType: string | null;
  _: string;
}
export interface IGetGridDimensionCalculationsReturn {
  screenHeight: number;
  screenWidth: number;
  gridSectionHeight: number;
  gridSectionWidth: number;
  gridHeight: number;
  disclaimerHeightOriginal: number;
  disclaimerHeightScaled: number;
  disclaimerWidthOriginal: number;
  extraHeightForDisclaimerTopAdjustment: number;
  disclaimerWidth: number;
  scale: number;
}

export const isCurrentScreenWithCOD = (
  screenIndex: number,
  globalComponentsScreen?: GlobalComponentsScreenInterface,
) =>
  globalComponentsScreen?.globalComponents.some(
    (component) =>
      component.componentType === ComponentTypes.COD &&
      component.enabled &&
      component.screenSequence === screenIndex,
  );

export const isCurrentScreenWithCODThankYou = (
  screenIndex: number,
  globalComponentsScreen?: GlobalComponentsScreenInterface,
) =>
  globalComponentsScreen?.globalComponents.some(
    (component) =>
      component.componentType === ComponentTypes.COD_THANK_YOU &&
      component.enabled &&
      component.screenSequence === screenIndex,
  );

export const isCurrentScreenWithDYR = (
  screenIndex: number,
  globalComponentsScreen?: GlobalComponentsScreenInterface,
) =>
  globalComponentsScreen?.globalComponents.some(
    (component) =>
      component.componentVariant === DyProductSource.DYR &&
      component.enabled &&
      component.screenSequence === screenIndex,
  );

export const isCurrentScreenWithDYS = (
  screenIndex: number,
  globalComponentsScreen?: GlobalComponentsScreenInterface,
) =>
  globalComponentsScreen?.globalComponents.some(
    (component) =>
      component.componentVariant === DyProductSource.DYS &&
      component.enabled &&
      component.screenSequence === screenIndex,
  );

export const isDYRDisabled = (
  globalComponentsScreen?: GlobalComponentsScreenInterface,
) =>
  globalComponentsScreen?.globalComponents.some((component) => {
    return (
      component.componentVariant === DyProductSource.DYR && !component.enabled
    );
  });

export const isDisabledScreen = (
  index: number,
  sectionTypes?: SectionTypes[],
  activeAssemblyJourney?: AssemblyJourneys,
  globalComponentsScreen?: GlobalComponentsScreenInterface,
) => {
  const screenIndex = index + 1;
  const isRotationOrTakeOver =
    activeAssemblyJourney === SectionJourney.SECTION_ROTATION ||
    activeAssemblyJourney === SectionJourney.TAKE_OVER;
  const { hasCOD, hasThankYou, hasDYR } = checkSectionTypes(sectionTypes);

  const isCODSection =
    isCurrentScreenWithCOD(screenIndex, globalComponentsScreen) && hasCOD;
  const isThankYouOrDYRSection =
    (isCurrentScreenWithCODThankYou(screenIndex, globalComponentsScreen) ||
      isCurrentScreenWithDYR(screenIndex, globalComponentsScreen)) &&
    (hasThankYou || hasDYR);

  return isRotationOrTakeOver && (isCODSection || isThankYouOrDYRSection);
};

export const parseLayoutConfig = (
  configString: string,
): IParseLayoutConfigReturn => {
  const regex =
    /^(horizontal|vertical)(\d+)x(\d+)Grid(?:With(Top|Bottom)(Full|Half)Disclaimer)?$/;
  const match = configString.match(regex);

  if (!match) throw new Error(lang.invalidLayoutConfig);

  const [_, orientation, cols, rows, disclaimerPosition, disclaimerType] =
    match;

  return {
    orientation,
    rows: parseInt(rows, 10),
    cols: parseInt(cols, 10),
    disclaimerPosition: disclaimerPosition || null,
    disclaimerType: disclaimerType || null,
    _,
  };
};

export const getGridDimensionCalculations = (
  props: IGridDimensionsParams,
): IGetGridDimensionCalculationsReturn => {
  const { orientation, disclaimerPosition, disclaimerType, rows, cols } = props;
  const isVertical = orientation === 'vertical';
  const screenHeight = isVertical ? 1920 : 1080;
  const screenWidth = isVertical ? 1080 : 1920;
  const scale = props?.scale ?? 0.3;

  // Calculate disclaimer height (default height without scaling)
  const disclaimerHeightOriginal = isVertical
    ? screenHeight / 12
    : screenHeight / 15;

  // Optionally scale the disclaimer height
  const disclaimerHeightScaled = disclaimerHeightOriginal * scale;

  // Calculate the grid height, accounting for the disclaimer position
  const gridHeight = disclaimerPosition
    ? screenHeight - disclaimerHeightOriginal
    : screenHeight;

  // Calculate grid section dimensions, applying scale if necessary
  const gridSectionHeight = (gridHeight / rows) * scale;
  const gridSectionWidth = (screenWidth / cols) * scale;

  const disclaimerWidthOriginal =
    disclaimerType === IDisclaimerWidth.FULL ? screenWidth : screenWidth / 2;

  const disclaimerWidth =
    disclaimerType === IDisclaimerWidth.FULL
      ? screenWidth * scale
      : (screenWidth * scale) / 2;

  const extraHeightForDisclaimerTopAdjustment =
    disclaimerPosition === IDisclaimerPosition.TOP
      ? disclaimerHeightOriginal
      : 0;

  return {
    screenHeight,
    screenWidth,
    gridSectionHeight,
    gridSectionWidth,
    gridHeight,
    disclaimerHeightOriginal,
    disclaimerHeightScaled,
    disclaimerWidthOriginal,
    extraHeightForDisclaimerTopAdjustment,
    disclaimerWidth,
    scale,
  };
};

export const getSectionSizeAndPositionForLayoutBuilder = (
  screenGridLine: string,
  width: number,
  height: number,
  leftOffset: number,
  topOffset: number,
): {
  x: number;
  y: number;
  w: number;
  h: number;
} => {
  const { orientation, rows, cols, disclaimerPosition, disclaimerType } =
    parseLayoutConfig(screenGridLine);
  const { disclaimerHeightOriginal, gridHeight, screenWidth, screenHeight } =
    getGridDimensionCalculations({
      orientation,
      disclaimerPosition,
      disclaimerType,
      rows,
      cols,
    });
  const actualHeight = disclaimerPosition ? gridHeight : screenHeight;
  const actualTopOffset =
    topOffset -
    (disclaimerPosition === IDisclaimerPosition.TOP
      ? disclaimerHeightOriginal
      : 0);

  return {
    x: Math.round((leftOffset * cols) / screenWidth),
    y: Math.round((actualTopOffset * rows) / actualHeight),
    w: Math.round((width * cols) / screenWidth),
    h: Math.round((height * rows) / actualHeight),
  };
};

export const getCurrentGlobalComponentsScreen = (
  globalComponentsScreens?: GlobalComponentsScreensInterface,
  layout?: ILayout,
  screenCount?: number,
  screenLayoutType?: string,
) => {
  const resolvedScreenCount = String(screenCount ?? layout?.screens.length);
  const resolvedScreenLayoutType =
    screenLayoutType ?? layout?.config?.screenLayoutType;
  const currentGlobalComponentsScreen =
    globalComponentsScreens?.[
      Object.keys(globalComponentsScreens).find((key) =>
        key.includes(resolvedScreenCount || ''),
      ) || ''
    ];

  return (
    currentGlobalComponentsScreen?.find(
      (item) => item.screenLayoutType === resolvedScreenLayoutType,
    ) || currentGlobalComponentsScreen?.[0]
  );
};
